import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, SIGNOUT_PENDING_KEY } from '../Contexts/AuthProvider';
import { appColors } from '../Config/Theme';

/** Marker that prevents an infinite IdP-logout redirect loop. */
const IDP_LOGOUT_DONE_KEY = 'eeas.signout.idpDone';

const readSessionFlag = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeSessionFlag = (key: string): void => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    /* sessionStorage unavailable (private mode) — best effort */
  }
};

const clearSessionFlag = (key: string): void => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* no-op */
  }
};

/**
 * Builds the Azure AD (Entra) logout URL that ends the federated SSO session so
 * the next sign-in re-prompts for the Exponent username. Returns null when the
 * feature is not configured. Reads env only — never hardcodes URLs.
 */
const buildIdpLogoutUrl = (): string | null => {
  const idpLogoutUrl = process.env.REACT_APP_IDP_LOGOUT_URL?.trim();
  const redirectSignout = process.env.REACT_APP_REDIRECT_SIGNOUT?.trim();
  if (!idpLogoutUrl || !redirectSignout) {
    return null;
  }
  const separator = idpLogoutUrl.includes('?') ? '&' : '?';
  return `${idpLogoutUrl}${separator}post_logout_redirect_uri=${encodeURIComponent(redirectSignout)}`;
};

/**
 * Public post-logout landing page. Cognito Hosted UI redirects here after the
 * user signs out (via REACT_APP_REDIRECT_SIGNOUT). It never requires
 * authentication, defensively clears cached data, and — when configured — chains
 * a single logout to Azure AD so the SSO session is fully ended.
 */
export default function SignedOutPage() {
  const { signIn } = useAuth();
  const queryClient = useQueryClient();

  // Decide synchronously whether we still need to end the federated IdP session,
  // so we don't flash the "signed out" message before redirecting.
  const [redirectingToIdp] = useState<boolean>(() => {
    if (!buildIdpLogoutUrl()) return false;
    const initiatedSignOut = readSessionFlag(SIGNOUT_PENDING_KEY);
    const alreadyLoggedOutOfIdp = readSessionFlag(IDP_LOGOUT_DONE_KEY);
    return initiatedSignOut && !alreadyLoggedOutOfIdp;
  });

  useEffect(() => {
    // Defensively clear any cached auth-sensitive queries that may remain.
    queryClient.clear();

    if (redirectingToIdp) {
      const url = buildIdpLogoutUrl();
      if (url) {
        // Guard against a redirect loop when Azure AD sends the browser back here.
        writeSessionFlag(IDP_LOGOUT_DONE_KEY);
        window.location.assign(url);
        return;
      }
    }

    // Final landing (feature off, or we've returned from the IdP) — clean up markers.
    clearSessionFlag(SIGNOUT_PENDING_KEY);
    clearSessionFlag(IDP_LOGOUT_DONE_KEY);
  }, [queryClient, redirectingToIdp]);

  if (redirectingToIdp) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: appColors.surface.page,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ color: appColors.text.body }}>
            Signing you out…
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: appColors.surface.page,
        px: 2,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          textAlign: 'center',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: appColors.primary.main }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: appColors.text.heading }}>
            You have been signed out of EEAS.
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body }}>
            Your session has ended. Sign in again to return to the Emergency Evacuation
            Accountability System.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => signIn()}
            sx={{ mt: 1, minWidth: 200 }}
          >
            Sign in again
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
